import { useCallback, useState } from 'react'
import { useClient } from 'urql'
import { STATEMENTS_PARTICIPATING_IN_MODULE_TC } from 'src/graphql/statements'

export const MODULE_TC_TYPES = new Set([
  'tarjetadecreditonacional',
  'tarjetadecreditointernacional',
  'rendiciontarjetacreditonacional',
  'rendiciontarjetacreditointernacional',
])

export function isModuleTcObligationMeta(meta) {
  if (!meta) return false
  const type = String(meta.type || '').trim().toLowerCase()
  if (MODULE_TC_TYPES.has(type)) return true
  return Boolean(String(meta.statementId || '').trim())
}

export function obligationIdFromGenerateMatchInput(input) {
  const origin = String(input?.originType || '').toUpperCase()
  const end = String(input?.endType || '').toUpperCase()
  if (origin === 'TRANSACTION' && end === 'OBLIGATION') return String(input.endId || '').trim()
  if (end === 'TRANSACTION' && origin === 'OBLIGATION') return String(input.originId || '').trim()
  return ''
}

export function isTransactionObligationMatchInput(input) {
  return Boolean(obligationIdFromGenerateMatchInput(input))
}

export function pairFromMultipleInput(input) {
  const origin = Array.isArray(input?.origin) ? input.origin[0] : null
  const end = Array.isArray(input?.end) ? input.end[0] : null
  if (!origin || !end) return {}
  return {
    originType: origin.originType,
    originId: origin.originId,
    endType: end.endType,
    endId: end.endId,
  }
}

export function isModuleTcStatementAssignable(row) {
  if (!row?.statementId) return false
  if (row.isLocked === true) return false
  if (row.hasIntactBankSnapshot === true) return false
  if (row.hasOpenBankCupo === false) return false
  return true
}

export function canAutoAnnotateSingleModuleTcStatement(row) {
  if (!row?.statementId) return false
  if (row.isLocked === true) return false
  if (row.hasIntactBankSnapshot === true) return false
  return true
}

export function useModuleTcStatementPicker(companyId) {
  const client = useClient()
  const [open, setOpen] = useState(false)
  const [pendingInput, setPendingInput] = useState(null)
  const [participants, setParticipants] = useState([])
  const [loading, setLoading] = useState(false)

  const fetchParticipants = useCallback(
    async (obligationId) => {
      const id = String(obligationId || '').trim()
      if (!companyId || !id) return []
      const result = await client
        .query(
          STATEMENTS_PARTICIPATING_IN_MODULE_TC,
          { companyId, obligationId: id },
          { requestPolicy: 'network-only' },
        )
        .toPromise()
      return result?.data?.statementsParticipatingInModuleTc || []
    },
    [client, companyId],
  )

  const attachOrPrompt = useCallback(
    async (input, obligationMeta) => {
      if (!isTransactionObligationMatchInput(input)) return { input, prompted: false }
      if (!isModuleTcObligationMeta(obligationMeta)) return { input, prompted: false }
      const obligationId = obligationIdFromGenerateMatchInput(input)
      setLoading(true)
      try {
        const list = await fetchParticipants(obligationId)
        if (list.length <= 1) {
          const only = list[0]
          if (canAutoAnnotateSingleModuleTcStatement(only)) {
            return { input: { ...input, creditCardStatementId: only.statementId }, prompted: false }
          }
          return { input, prompted: false }
        }
        setPendingInput(input)
        setParticipants(list)
        setOpen(true)
        return { input: null, prompted: true }
      } finally {
        setLoading(false)
      }
    },
    [fetchParticipants],
  )

  const confirm = useCallback((statementId) => {
    const next = pendingInput ? { ...pendingInput, creditCardStatementId: statementId } : null
    setOpen(false)
    setPendingInput(null)
    setParticipants([])
    return next
  }, [pendingInput])

  const cancel = useCallback(() => {
    setOpen(false)
    setPendingInput(null)
    setParticipants([])
  }, [])

  return { open, participants, loading, attachOrPrompt, confirm, cancel }
}
